/*
 * Brake House — WAIR recommendation capture (PDP)
 * ----------------------------------------------
 * WAIR (app embed "wair-size-fit-recommendation", predict-v4.getwair.com/default/predict.js)
 * tells the customer which jacket size to buy, but it never writes that answer onto the order.
 * CS then has no idea whether the customer followed the advice or overrode it. This script
 * listens for WAIR's recommendation and parks it on the cart line as a hidden property:
 *
 *   properties[_wair_recommended_size]
 *
 * Read by the CS dashboard (brake-house-ops CustomerMeasurements.tsx -> "WAIR size").
 * Boots + studded belts hide WAIR entirely (our own recommenders replace it), so this only
 * ever runs where snippets/wair.liquid rendered a .predict-placement.
 *
 * WAIR's public browser API is a set of window CustomEvents:
 *   onPredictLoad          detail: { predict }   (predict.render({...}) entry point)
 *   onPredictModalOpen     no detail
 *   onPredictModalClose    no detail
 *   onPredictPrediction    no detail  <- fires when a prediction is made, but carries nothing
 *   onPredictRecommendations  detail: <payload>  <- the only event carrying the answer
 *
 * Observed payload (widget v4.63.10, verified end-to-end on the v14 preview 2026-07-17) --
 * an ARRAY, one entry per recommended product:
 *
 *   [ { options: { Size: "XL" },
 *       productKey: "j-31-leather-motorcycle-jacket",
 *       variantId: "50678617604409" } ]
 *
 * We read options.Size off the entry whose productKey matches this PDP. That shape is not a
 * contract though (predict.js is minified, the modal ships from predict-v4a.getwair.com, and
 * the app self-updates), so if it ever changes we fall back to walking the payload for any
 * value matching a size this product sells, and finally to the WAIR button, which reads
 * "Your size: XL" once a prediction exists. Worst case we write nothing and the dashboard
 * omits the row -- this must never block add-to-cart.
 */
(function () {
  "use strict";

  var PROP = "_wair_recommended_size";
  var DEBUG = /[?&]bh_wair_debug=1/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ["[bh-wair]"].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {}
  }

  var placement = document.querySelector(".predict-placement[data-predict-product-key]");
  if (!placement) return; // WAIR isn't on this PDP (boots / belts / non-apparel).

  // ---- the sizes this product actually sells, straight off the chips WAIR itself keys on.
  function sizeOptionValues() {
    var scope = document.querySelector('[data-wair-option-name="Size"]');
    if (!scope) return [];
    var out = [];
    var els = scope.querySelectorAll("[data-wair-option-value]");
    Array.prototype.forEach.call(els, function (el) {
      var v = (el.getAttribute("data-wair-option-value") || "").trim();
      if (v && out.indexOf(v) === -1) out.push(v);
    });
    return out;
  }

  function norm(s) {
    return String(s).trim().toLowerCase().replace(/\s+/g, "");
  }

  // The known payload path: detail[] -> { productKey, options: { Size } }. Prefer the entry
  // matching this PDP, since WAIR can return recommendations for several products at once.
  function findSizeInKnownShape(payload, sizes) {
    var list = Object.prototype.toString.call(payload) === "[object Array]" ? payload : [payload];
    var key = placement.getAttribute("data-predict-product-key");
    var match = null;
    var fallback = null;
    for (var i = 0; i < list.length; i++) {
      var rec = list[i];
      if (!rec || typeof rec !== "object" || !rec.options) continue;
      var size = rec.options.Size || rec.options.size;
      if (!size) continue;
      if (rec.productKey === key) { match = String(size).trim(); break; }
      if (fallback === null) fallback = String(size).trim();
    }
    var picked = match || fallback;
    if (!picked) return null;
    // Only trust it if it is a size this product actually sells.
    for (var s = 0; s < sizes.length; s++) {
      if (norm(sizes[s]) === norm(picked)) {
        log("known shape ->", sizes[s], match ? "(productKey matched)" : "(first entry)");
        return sizes[s];
      }
    }
    log("known shape gave", picked, "which is not an option on this product; ignoring");
    return null;
  }

  // A key is "recommendation-ish" if it smells like WAIR's answer rather than incidental data.
  var GOOD_KEY = /(recommend|bestsize|best_size|predictedsize|predicted_size|suggested|sizelabel|size_label|^size$|^label$|^value$|^name$)/i;

  // Walk the payload breadth-first; return the first value matching a real size option,
  // preferring matches found under a recommendation-ish key. Depth/'breadth capped so a
  // pathological payload can't hang the page.
  function findSizeInPayload(payload, sizes) {
    if (!payload || !sizes.length) return null;
    var wanted = sizes.map(norm);
    var queue = [{ key: "", val: payload, depth: 0 }];
    var seen = [];
    var loose = null;
    var visits = 0;

    while (queue.length && visits < 500) {
      var node = queue.shift();
      visits++;
      var val = node.val;
      if (val === null || val === undefined) continue;

      var t = typeof val;
      if (t === "string" || t === "number") {
        var i = wanted.indexOf(norm(val));
        if (i !== -1) {
          if (GOOD_KEY.test(node.key)) {
            log("matched size", sizes[i], "at key", node.key);
            return sizes[i]; // strong match -- take it immediately
          }
          if (loose === null) loose = sizes[i]; // remember, but keep looking for a better key
        }
        continue;
      }
      if (t !== "object" || node.depth >= 6) continue;
      if (seen.indexOf(val) !== -1) continue; // cycle guard
      seen.push(val);

      if (Object.prototype.toString.call(val) === "[object Array]") {
        for (var a = 0; a < val.length && a < 50; a++) {
          queue.push({ key: node.key, val: val[a], depth: node.depth + 1 });
        }
        continue;
      }
      for (var k in val) {
        if (!Object.prototype.hasOwnProperty.call(val, k)) continue;
        try {
          queue.push({ key: k, val: val[k], depth: node.depth + 1 });
        } catch (e) {}
      }
    }
    if (loose) log("matched size", loose, "(loose key)");
    return loose;
  }

  // Fallback: once WAIR has a prediction its button reads e.g. "Your size: L".
  function findSizeInButton(sizes) {
    var btn = document.querySelector("#wair-fit-advisor");
    if (!btn || !sizes.length) return null;
    var text = (btn.textContent || "").trim();
    if (!text) return null;
    // Longest-first so "2XL" wins over "XL" on the same label.
    var ordered = sizes.slice().sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < ordered.length; i++) {
      var re = new RegExp("(^|[^A-Za-z0-9])" + ordered[i].replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "([^A-Za-z0-9]|$)", "i");
      if (re.test(text)) {
        log("matched size", ordered[i], "from button text:", text);
        return ordered[i];
      }
    }
    return null;
  }

  // ---- cart-line property plumbing (same pattern as bh-belt-size-checker.js / bh-size-recommender.js)
  function findForm_() {
    return (
      (placement.closest && placement.closest('form[action*="/cart"]')) ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function setCartProp_(value) {
    var form = findForm_();
    if (!form) return;
    var input = form.querySelector('input[data-bhwair-prop="' + PROP + '"]');
    if (!value) {
      if (input && input.parentNode) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "properties[" + PROP + "]";
      input.setAttribute("data-bhwair-prop", PROP);
      form.appendChild(input);
    }
    input.value = value;
  }

  var captured = null;
  function capture(size) {
    if (!size || size === captured) return;
    captured = size;
    setCartProp_(size);
    log("captured ->", PROP, "=", size);
  }

  window.addEventListener("onPredictRecommendations", function (event) {
    try {
      var sizes = sizeOptionValues();
      var detail = event && event.detail;
      log("onPredictRecommendations", detail, "| product sizes:", sizes);
      capture(findSizeInKnownShape(detail, sizes) || findSizeInPayload(detail, sizes));
    } catch (e) {
      log("payload read failed", e);
    }
  });

  // onPredictPrediction carries no detail, and the modal can close right after the answer
  // renders. Both are cues to re-read the button in case the payload walk came up empty.
  function sweepButton() {
    if (captured) return;
    try {
      capture(findSizeInButton(sizeOptionValues()));
    } catch (e) {
      log("button read failed", e);
    }
  }
  window.addEventListener("onPredictPrediction", function () { setTimeout(sweepButton, 400); });
  window.addEventListener("onPredictModalClose", function () { setTimeout(sweepButton, 400); });

  // A returning visitor's prediction is restored by WAIR on load with no event of its own,
  // so take one late look at the button.
  if (document.readyState === "complete") setTimeout(sweepButton, 2500);
  else window.addEventListener("load", function () { setTimeout(sweepButton, 2500); });

  if (DEBUG) window.__bhWair = { get captured() { return captured; }, sizes: sizeOptionValues, sweep: sweepButton };
})();
